import { Progress, Tag, Tooltip } from 'antd';
import type { ReactNode } from 'react';

import {
  ProjectTaskProgressMethod,
  ProjectTaskStatus,
  projectTaskDependencyTypeNames,
  projectTaskProgressMethodNames,
  projectTaskStatusNames,
} from '../../api/projects';
import type {
  ProjectTaskDependencyTypeValue,
  ProjectTaskItem,
  ProjectTaskProgressMethodValue,
  ProjectTaskStatusValue,
} from '../../api/projects';

/**
 * Phần hiển thị dùng chung của cây công việc.
 */

/**
 * Màu của từng trạng thái công việc.
 *
 * <b>Bị chặn dùng màu đỏ, không phải màu vàng.</b> Đó là trạng thái duy nhất ở đây cần ai đó hành
 * động ngay, và người phải hành động thường không phải người đang làm việc đó — nguyên nhân hay nằm
 * ở phía khách. Tô nhạt sẽ làm nó lẫn vào những dòng bình thường.
 */
const statusColors: Readonly<Record<ProjectTaskStatusValue, string>> = {
  [ProjectTaskStatus.NotStarted]: 'default',
  [ProjectTaskStatus.InProgress]: 'processing',
  [ProjectTaskStatus.Blocked]: 'red',
  [ProjectTaskStatus.PendingReview]: 'gold',
  [ProjectTaskStatus.Completed]: 'success',
  [ProjectTaskStatus.Cancelled]: 'default',
};

/** Nhãn trạng thái công việc, kèm lý do khi bị chặn. */
export function taskStatusTag(
  status: ProjectTaskStatusValue,
  blockedReason: string | null,
): ReactNode {
  const tag = <Tag color={statusColors[status]}>{projectTaskStatusNames[status]}</Tag>;

  if (status !== ProjectTaskStatus.Blocked || blockedReason === null) {
    return tag;
  }

  return <Tooltip title={blockedReason}>{tag}</Tooltip>;
}

/**
 * Ô tiến độ của một dòng công việc.
 *
 * <b>Việc nhóm hiển thị khác việc lá.</b> Con số của việc nhóm là kết quả tính từ các việc con, nên
 * nó được ghi rõ là <i>tính từ việc con</i> — người đọc cần biết mình đang nhìn một con số phái sinh
 * chứ không phải con số ai đó báo.
 *
 * <b>Tiến độ tự báo được đánh dấu riêng.</b> Đây là điểm mấu chốt của cả thiết kế chống bệnh
 * <i>"90% mãi không xong"</i>: con số dựa trên cảm nhận phải nhìn ra được ngay, để người đọc biết
 * cần hỏi thêm.
 */
export function taskProgressCell(task: ProjectTaskItem): ReactNode {
  const percent = Math.round(task.progressPercent);

  const hint = task.isSummaryTask
    ? 'Tính từ các việc con, không nhập tay được.'
    : task.isProgressSelfReported
      ? 'Người làm tự báo — con số này dựa trên cảm nhận, không phải thứ đếm được.'
      : 'Đo theo cách xong hoặc chưa xong.';

  return (
    <Tooltip title={hint}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Progress
          percent={percent}
          size="small"
          status={task.isOverdue ? 'exception' : 'normal'}
          style={{ flex: 1, minWidth: 70, margin: 0 }}
        />
        {task.isProgressSelfReported ? (
          <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>
            tự báo
          </Tag>
        ) : null}
      </div>
    </Tooltip>
  );
}

/** Các lựa chọn trạng thái công việc. */
export const taskStatusOptions = Object.entries(projectTaskStatusNames).map(
  ([value, label]) => ({
    value: Number(value) as ProjectTaskStatusValue,
    label,
  }),
);

/**
 * Các lựa chọn cách đo tiến độ, đã bỏ hai cách chưa dựng.
 *
 * Hiện chúng ra rồi để máy chủ từ chối là cách chắc chắn làm người dùng mất lòng tin: họ chọn một
 * mục có sẵn và nhận về một thông báo nói mục đó không dùng được.
 */
export const taskProgressMethodOptions: {
  value: ProjectTaskProgressMethodValue;
  label: string;
}[] = [ProjectTaskProgressMethod.ZeroOrHundred, ProjectTaskProgressMethod.SelfReported].map(
  (value) => ({ value, label: projectTaskProgressMethodNames[value] }),
);

/** Các lựa chọn kiểu phụ thuộc. */
export const dependencyTypeOptions = Object.entries(projectTaskDependencyTypeNames).map(
  ([value, label]) => ({
    value: Number(value) as ProjectTaskDependencyTypeValue,
    label,
  }),
);

/**
 * Khoảng thụt lề của một dòng theo cấp trong cây.
 *
 * Bảng phẳng có thụt lề đọc nhanh hơn hẳn một cây bấm mở từng nhánh khi số dòng lên tới vài trăm:
 * người dùng nhìn được cả bức tranh mà không phải bấm mở.
 */
export function taskIndent(depth: number): number {
  return depth * 18;
}
